import axios from 'axios';

import { encodeProductPathSegment } from '@apple/utils/url';

import { type FeaturedProduct, featuredProductSchema } from '../models/management';

/**
 * Gets the list of featured products.
 * @see AppleRetail.Web.Features.Catalog.FeaturedProductManagementController.GetAll
 */
export async function getFeaturedProducts(): Promise<FeaturedProduct[]> {
	const response = await axios.get('/api/management/products/featured/all');
	return featuredProductSchema.array().parse(response.data);
}

/**
 * Adds a product to the featured list (appended at the end).
 * @see AppleRetail.Web.Features.Catalog.FeaturedProductManagementController.Add
 * @param itemId The ID of the product to mark as featured.
 * @throws HTTP 409 if already featured, 400 if the list is full.
 */
export async function addFeaturedProduct(itemId: string): Promise<void> {
	await axios.post(`/api/management/products/featured/${encodeProductPathSegment(itemId)}`);
}

/**
 * Reorders an existing featured product to the specified 1-based index.
 * @see AppleRetail.Web.Features.Catalog.FeaturedProductManagementController.Update
 * @param itemId   The ID of the featured product to move.
 * @param newIndex The new 1-based position within the featured list.
 * @throws HTTP 400 if newIndex is out of range or no featured items, 404 if item not found.
 */
export async function updateFeaturedProductOrder(itemId: string, newIndex: number): Promise<void> {
	await axios.put(
		`/api/management/products/featured/${encodeProductPathSegment(itemId)}/${newIndex}`,
	);
}

/**
 * Removes a product from the featured list.
 * @see AppleRetail.Web.Features.Catalog.FeaturedProductManagementController.Delete
 * @param itemId The ID of the featured product to remove.
 * @throws HTTP 404 if item not found.
 */
export async function deleteFeaturedProduct(itemId: string): Promise<void> {
	await axios.delete(`/api/management/products/featured/${encodeProductPathSegment(itemId)}`);
}
